import {
  makeSelectedEvent,
  selectSelectedItems,
} from 'app/pages/TicketSalePage/slice/selectors';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Ticket, TicketTypeBundle } from 'types/TicketsState';
import BundledAddonsSection from '../BundledAddonsSection/BundledAddonsSection';
import TicketTypeBundleGenerics from './TicketTypeBundleGenerics';
import TicketTypeBundlePrice from './TicketTypeBundlePrice';
import TicketTypeBundleQuantityButtons from './TicketTypeBundleQuantityButtons';
import {
  bundleHasCalendar,
  getAddons,
  getSalePeriod,
  getTicketTypes,
} from 'utils/bundles';
import { getBooleansForDisplayingTicketLabels } from '../TicketSection/getBooleansForDisplayingTicketLabels';
import useTicketTypeBundleIsSoldOut from './useTicketTypeBundleIsSoldOut';
import { DateTime } from 'luxon';
import { useCalendarController } from '../TicketSaleCard/Calendar/calendarContext';
import { SelectedItems, SelectedItemType } from 'types/SelectedItem';
import { findIndex } from 'lodash';
import { dateFormatForCalendar } from 'utils/calendar';
import useEmbedResize from 'utils/custom-hooks/useEmbedResize';

interface TicketTypeBundleSectionProps {
  ticketTypeBundle: TicketTypeBundle;
}

const TicketTypeBundleSection: React.FC<TicketTypeBundleSectionProps> = ({
  ticketTypeBundle,
}) => {
  const { description } = ticketTypeBundle;
  const {
    tickets,
    timeInterval: { endsAt },
    dateTimeFormat: { timezone },
  } = useSelector(makeSelectedEvent);
  const selectedItems = useSelector(selectSelectedItems);

  // to be refactored ( quick and dirty solution )
  let selectedDate: DateTime | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { selectedDate: selectedDateFromContext } = useCalendarController();
    selectedDate = selectedDateFromContext;
  } catch (e) {}

  const ticketTypes = getTicketTypes(ticketTypeBundle.items, tickets);
  const bundledAddons = getAddons(ticketTypes, true);

  // quantity
  const index =
    bundleHasCalendar(ticketTypes) && selectedDate
      ? findIndex(selectedItems, {
          type: SelectedItemType.BUNDLE,
          id: ticketTypeBundle.id,
          date: selectedDate.toFormat(dateFormatForCalendar),
        })
      : findIndex(selectedItems, {
          type: SelectedItemType.BUNDLE,
          id: ticketTypeBundle.id,
        });
  const [quantity, setQuantity] = useState(
    index === -1 ? 0 : selectedItems[index].quantity,
  );

  // update state after setAllSelectedItems is dispatched
  // we should implement a better solution in the future
  if (
    selectedItems[index]?.quantity &&
    selectedItems[index]?.quantity !== quantity
  ) {
    setQuantity(selectedItems[index].quantity);
  }

  // useEmbedResize([quantity]);

  const soldOut = useTicketTypeBundleIsSoldOut({
    items: ticketTypeBundle.items,
    selectedDate,
  });

  const { sold_from, sold_to } = getSalePeriod(ticketTypes);
  const { displaySoldFromDate, displaySoldToDate, ticketIsSellable } =
    getBooleansForDisplayingTicketLabels(
      endsAt,
      timezone,
      sold_from,
      sold_to,
      soldOut,
    );

  return (
    <div className="border-b border-neutral-40 px-[12px] relative">
      <div className="relative flex items-start sm:items-center justify-between sm:px-[12px] pb-[32px] pt-[12px] sm:pb-[12px]">
        <TicketTypeBundleGenerics
          ticketTypeBundle={ticketTypeBundle}
          ticketTypes={ticketTypes}
          displaySoldFromDate={displaySoldFromDate}
          displaySoldToDate={displaySoldToDate}
          sold_from={sold_from}
          sold_to={sold_to}
          soldOut={soldOut}
          selectedDate={selectedDate}
        />
        <TicketTypeBundlePrice
          ticketTypeBundle={ticketTypeBundle}
          ticketTypes={ticketTypes}
        />
        <TicketTypeBundleQuantityButtons
          id={ticketTypeBundle.id}
          quantity={quantity}
          setQuantity={setQuantity}
          ticketIsSellable={ticketIsSellable}
          selectedDate={selectedDate}
        />
      </div>
      {description ? (
        <div className="sm:px-[12px] pb-[12px] whitespace-pre-line p3-regular text-neutral-800">
          {description}
        </div>
      ) : null}
      {bundledAddons.length > 0 ? (
        <BundledAddonsSection addons={bundledAddons} />
      ) : null}
    </div>
  );
};

export default TicketTypeBundleSection;
